import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Users, Plus, Mail, Phone, Award, Edit, Upload, Trash2, Package, Briefcase, Camera } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Photographers({ user }) {
  const [photographers, setPhotographers] = useState([]);
  const [gearKits, setGearKits] = useState([]);
  const [gearItems, setGearItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedPhotographer, setSelectedPhotographer] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    max_daily_hours: 12,
    preferred_break_mins: 30,
    skills: [],
    system_mount_preferences: [],
    personal_gear: []
  });
  const [gearInput, setGearInput] = useState({ name: '', type: 'lens', details: '' });
  const [uploadFile, setUploadFile] = useState(null);

  useEffect(() => {
    fetchPhotographers();
  }, []);

  const fetchPhotographers = async () => {
    try {
      const [photogsRes, kitsRes, itemsRes] = await Promise.all([
        axios.get(`${API}/photographers`),
        axios.get(`${API}/gear/kits`).catch(() => ({ data: [] })),
        axios.get(`${API}/gear/items`).catch(() => ({ data: [] })),
      ]);
      setPhotographers(photogsRes.data);
      setGearKits(kitsRes.data || []);
      setGearItems(itemsRes.data || []);
    } catch (error) {
      toast.error('Failed to fetch photographers');
    } finally {
      setLoading(false);
    }
  };

  // Group held gear by photographer for fast lookup.
  const heldByPhotographer = useMemo(() => {
    const map = {};
    gearKits.forEach((k) => {
      if (k.owner_photographer_id) {
        const id = k.owner_photographer_id;
        map[id] = map[id] || { kits: [], items: [] };
        map[id].kits.push(k);
      }
    });
    gearItems.forEach((it) => {
      if (it.owner_photographer_id) {
        const id = it.owner_photographer_id;
        map[id] = map[id] || { kits: [], items: [] };
        map[id].items.push(it);
      }
    });
    return map;
  }, [gearKits, gearItems]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/photographers`, formData);
      toast.success('Photographer added successfully!');
      setOpen(false);
      fetchPhotographers();
      resetForm();
    } catch (error) {
      toast.error('Failed to add photographer');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/photographers/${selectedPhotographer.id}`, formData);
      toast.success('Photographer updated successfully!');
      setEditOpen(false);
      fetchPhotographers();
      resetForm();
    } catch (error) {
      toast.error('Failed to update photographer');
    }
  };

  const openEditDialog = (photographer) => {
    setSelectedPhotographer(photographer);
    setFormData({
      name: photographer.name,
      email: photographer.email,
      phone: photographer.phone || '',
      max_daily_hours: photographer.max_daily_hours,
      preferred_break_mins: photographer.preferred_break_mins,
      skills: photographer.skills || [],
      system_mount_preferences: photographer.system_mount_preferences || [],
      personal_gear: photographer.personal_gear || []
    });
    setEditOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      max_daily_hours: 12,
      preferred_break_mins: 30,
      skills: [],
      system_mount_preferences: [],
      personal_gear: []
    });
    setSelectedPhotographer(null);
    setGearInput({ name: '', type: 'lens', details: '' });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this photographer?')) {
      try {
        await axios.delete(`${API}/photographers/${id}`);
        toast.success('Photographer deleted');
        fetchPhotographers();
      } catch (error) {
        toast.error('Failed to delete photographer');
      }
    }
  };

  const addGearItem = () => {
    if (!gearInput.name.trim()) {
      toast.error('Please enter gear name');
      return;
    }
    setFormData(prev => ({
      ...prev,
      personal_gear: [...prev.personal_gear, { ...gearInput }]
    }));
    setGearInput({ name: '', type: 'lens', details: '' });
    toast.success('Gear item added');
  };

  const removeGearItem = (index) => {
    setFormData(prev => ({
      ...prev,
      personal_gear: prev.personal_gear.filter((_, i) => i !== index)
    }));
  };

  const downloadSampleCSV = () => {
    const csvContent = `Name,Email,Gear1,Gear2,Gear3,Gear4
John Smith,john@photo.com,Canon 5D Mark IV,70-200mm f/2.8,Manfrotto Tripod,Flash 600EX
Sarah Jones,sarah@photo.com,Nikon D850,24-70mm f/2.8,DJI Mavic Pro,Camera Bag
Mike Chen,mike@photo.com,Sony A7R V,85mm f/1.4,16-35mm f/2.8,Gimbal`;
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample_photographers.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success('Sample CSV downloaded');
  };

  const handleFileUpload = async () => {
    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', uploadFile);

      toast.info('Parsing file...');
      const response = await axios.post(`${API}/ai/parse-photographer-gear`, formDataUpload);
      
      const parsedData = response.data.parsed_data;
      
      if (!parsedData || !parsedData.photographers || parsedData.photographers.length === 0) {
        toast.error('No photographers found in file. Please check the format.');
        return;
      }

      toast.success(`Found ${parsedData.photographers.length} photographer(s). Importing...`);
      
      // Import the parsed photographers
      const importResponse = await axios.post(`${API}/photographers/bulk-import`, parsedData);
      
      toast.success(importResponse.data.message);
      setUploadOpen(false);
      setUploadFile(null);
      fetchPhotographers();
    } catch (error) {
      console.error('Upload error:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to process file';
      toast.error(errorMsg);
    }
  };

  return (
    <div className="flex min-h-screen">
      <Sidebar user={user} />
      <div className="main-content flex-1 w-full overflow-x-hidden">
        <div className="page-header flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 sm:p-6">
          <div>
            <h1 className="page-title">Photographers</h1>
            <p className="page-subtitle">Manage your photography team and their personal gear</p>
          </div>
          <div className="flex gap-3">
            <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-2" data-testid="upload-list-btn">
                  <Upload className="w-5 h-5 mr-2" />
                  Upload List
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Upload Photographer & Gear List</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                    <Upload className="w-12 h-12 mx-auto mb-3 text-slate-400" />
                    <input
                      type="file"
                      accept=".csv,.txt"
                      onChange={(e) => setUploadFile(e.target.files[0])}
                      className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
                      data-testid="file-upload-input"
                    />
                    <p className="text-xs text-slate-500 mt-2">CSV or TXT format</p>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-semibold text-blue-900">Sample CSV Format:</p>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={downloadSampleCSV}
                        className="text-blue-700 hover:text-blue-900"
                        data-testid="download-sample-btn"
                      >
                        Download Sample
                      </Button>
                    </div>
                    <div className="bg-white rounded p-3 font-mono text-xs text-slate-800 overflow-x-auto">
                      <div>Name,Email,Gear1,Gear2,Gear3</div>
                      <div>John Smith,john@photo.com,Canon 5D Mark IV,70-200mm f/2.8,Tripod</div>
                      <div>Sarah Jones,sarah@photo.com,Nikon D850,24-70mm f/2.8,DJI Mavic Pro</div>
                    </div>
                    <p className="text-blue-700 text-xs mt-2">
                      • First row can be headers (optional)<br/>
                      • Name and Email are required<br/>
                      • Add as many gear columns as needed<br/>
                      • AI will automatically categorize gear types
                    </p>
                  </div>

                  {uploadFile && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                      <p className="text-sm text-emerald-900">
                        <strong>Selected:</strong> {uploadFile.name} ({(uploadFile.size / 1024).toFixed(1)} KB)
                      </p>
                    </div>
                  )}
                  
                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => { setUploadOpen(false); setUploadFile(null); }} data-testid="cancel-upload-btn">
                      Cancel
                    </Button>
                    <Button onClick={handleFileUpload} className="btn-gradient" disabled={!uploadFile} data-testid="process-upload-btn">
                      <Upload className="w-4 h-4 mr-2" />
                      Import Photographers
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            <Dialog 
              open={open} 
              onOpenChange={(isOpen) => {
                setOpen(isOpen);
                if (isOpen) {
                  resetForm(); // Reset form when opening Add dialog
                }
              }}
            >
              <DialogTrigger asChild>
                <Button className="btn-gradient" data-testid="add-photographer-btn">
                  <Plus className="w-5 h-5 mr-2" />
                  Add Photographer
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add Photographer</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      placeholder="John Photographer"
                      data-testid="photographer-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      placeholder="john@example.com"
                      data-testid="photographer-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+61 400 000 000"
                      data-testid="photographer-phone-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="max_hours">Max Daily Hours</Label>
                      <Input
                        id="max_hours"
                        type="number"
                        value={formData.max_daily_hours}
                        onChange={(e) => setFormData({ ...formData, max_daily_hours: parseFloat(e.target.value) })}
                        data-testid="photographer-max-hours-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="break_mins">Preferred Break (mins)</Label>
                      <Input
                        id="break_mins"
                        type="number"
                        value={formData.preferred_break_mins}
                        onChange={(e) => setFormData({ ...formData, preferred_break_mins: parseInt(e.target.value) })}
                        data-testid="photographer-break-mins-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Label>Personal Gear Pack</Label>
                    <div className="border rounded-lg p-4 bg-slate-50">
                      <div className="grid grid-cols-12 gap-2 mb-3">
                        <Input
                          placeholder="Gear name"
                          value={gearInput.name}
                          onChange={(e) => setGearInput({ ...gearInput, name: e.target.value })}
                          className="col-span-5"
                          data-testid="gear-name-input"
                        />
                        <select
                          value={gearInput.type}
                          onChange={(e) => setGearInput({ ...gearInput, type: e.target.value })}
                          className="col-span-3 h-10 px-3 bg-white border border-slate-200 rounded-lg text-sm"
                          data-testid="gear-type-select"
                        >
                          <option value="body">Camera Body</option>
                          <option value="lens">Lens</option>
                          <option value="drone">Drone</option>
                          <option value="accessory">Accessory</option>
                          <option value="tripod">Tripod</option>
                        </select>
                        <Input
                          placeholder="Details"
                          value={gearInput.details}
                          onChange={(e) => setGearInput({ ...gearInput, details: e.target.value })}
                          className="col-span-3"
                          data-testid="gear-details-input"
                        />
                        <Button
                          type="button"
                          onClick={addGearItem}
                          size="sm"
                          className="col-span-1 bg-emerald-500 hover:bg-emerald-600"
                          data-testid="add-gear-btn"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>

                      {formData.personal_gear.length === 0 ? (
                        <p className="text-sm text-slate-500 text-center py-4">No gear added yet</p>
                      ) : (
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {formData.personal_gear.map((gear, index) => (
                            <div key={index} className="flex items-center justify-between p-2 bg-white rounded border border-slate-200" data-testid={`gear-item-${index}`}>
                              <div className="flex-1">
                                <p className="font-medium text-sm">{gear.name}</p>
                                <p className="text-xs text-slate-600">
                                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded mr-2">{gear.type}</span>
                                  {gear.details}
                                </p>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeGearItem(index)}
                                className="text-red-500 hover:text-red-600"
                                data-testid={`remove-gear-${index}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-slate-500">{formData.personal_gear.length} item(s) in gear pack</p>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setOpen(false);
                        resetForm();
                      }}
                      data-testid="cancel-photographer-btn"
                    >
                      Cancel
                    </Button>
                    <Button type="submit" className="btn-gradient" data-testid="submit-photographer-btn">
                      Add Photographer
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {loading ? (
          <div className="loading-spinner">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
          </div>
        ) : photographers.length === 0 ? (
          <Card className="glass-card p-12 text-center">
            <Users className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No photographers yet</h3>
            <p className="text-slate-600 mb-6">Add photographers individually or upload a list</p>
            <div className="flex gap-3 justify-center">
              <Button onClick={() => setUploadOpen(true)} variant="outline" className="border-2" data-testid="upload-first-list-btn">
                <Upload className="w-5 h-5 mr-2" />
                Upload List
              </Button>
              <Button onClick={() => setOpen(true)} className="btn-gradient" data-testid="add-first-photographer-btn">
                <Plus className="w-5 h-5 mr-2" />
                Add Manually
              </Button>
            </div>
          </Card>
        ) : (
          <div className="grid-3">
            {photographers.map((photographer) => (
              <Card key={photographer.id} className="glass-card p-6" data-testid={`photographer-card-${photographer.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(photographer)}
                      className="text-blue-500 hover:text-blue-600"
                      data-testid={`edit-photographer-${photographer.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(photographer.id)}
                      className="text-red-500 hover:text-red-600"
                      data-testid={`delete-photographer-${photographer.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">{photographer.name}</h3>
                <div className="space-y-2 text-sm text-slate-600">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    {photographer.email}
                  </div>
                  {photographer.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      {photographer.phone}
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Award className="w-4 h-4" />
                    Max {photographer.max_daily_hours}h/day
                  </div>
                </div>

                {photographer.personal_gear && photographer.personal_gear.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Package className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm font-medium text-slate-700">Personal Gear Pack</span>
                    </div>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {photographer.personal_gear.map((gear, idx) => (
                        <div key={idx} className="text-xs bg-slate-50 p-2 rounded">
                          <span className="font-medium">{gear.name}</span>
                          <span className="text-slate-500 ml-2">({gear.type})</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-slate-500 mt-2">{photographer.personal_gear.length} items</p>
                  </div>
                )}

                {(heldByPhotographer[photographer.id]?.kits?.length || heldByPhotographer[photographer.id]?.items?.length) ? (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Briefcase className="w-4 h-4 text-amber-600" />
                      <span className="text-sm font-medium text-slate-700">Equipment held</span>
                    </div>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {heldByPhotographer[photographer.id]?.kits?.map((kit) => (
                        <div key={`k-${kit.id}`} className="text-xs bg-amber-50 border border-amber-100 p-2 rounded flex items-center gap-2">
                          <Briefcase className="w-3 h-3 text-amber-700 flex-shrink-0" />
                          <span className="font-medium truncate">{kit.name}</span>
                          <span className="text-amber-700 ml-auto">{kit.type}</span>
                        </div>
                      ))}
                      {heldByPhotographer[photographer.id]?.items?.map((item) => (
                        <div key={`i-${item.id}`} className="text-xs bg-amber-50 border border-amber-100 p-2 rounded flex items-center gap-2">
                          <Camera className="w-3 h-3 text-amber-700 flex-shrink-0" />
                          <span className="font-medium truncate">{item.name}</span>
                          <span className="text-amber-700 ml-auto capitalize">{item.type}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                {photographer.skills && photographer.skills.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {photographer.skills.map((skill, idx) => (
                      <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                        {skill}
                      </span>
                    ))}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}

        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Photographer</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEdit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Name</Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="John Photographer"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder="john@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-phone">Phone</Label>
                <Input
                  id="edit-phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+61 400 000 000"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-max_hours">Max Daily Hours</Label>
                  <Input
                    id="edit-max_hours"
                    type="number"
                    value={formData.max_daily_hours}
                    onChange={(e) => setFormData({ ...formData, max_daily_hours: parseFloat(e.target.value) })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-break_mins">Preferred Break (mins)</Label>
                  <Input
                    id="edit-break_mins"
                    type="number"
                    value={formData.preferred_break_mins}
                    onChange={(e) => setFormData({ ...formData, preferred_break_mins: parseInt(e.target.value) })}
                  />
                </div>
              </div>

              <div className="space-y-3">
                <Label>Personal Gear Pack</Label>
                <div className="border rounded-lg p-4 bg-slate-50">
                  <div className="grid grid-cols-12 gap-2 mb-3">
                    <Input
                      placeholder="Gear name"
                      value={gearInput.name}
                      onChange={(e) => setGearInput({ ...gearInput, name: e.target.value })}
                      className="col-span-5"
                    />
                    <select
                      value={gearInput.type}
                      onChange={(e) => setGearInput({ ...gearInput, type: e.target.value })}
                      className="col-span-3 h-10 px-3 bg-white border border-slate-200 rounded-lg text-sm"
                    >
                      <option value="body">Camera Body</option>
                      <option value="lens">Lens</option>
                      <option value="drone">Drone</option>
                      <option value="accessory">Accessory</option>
                      <option value="tripod">Tripod</option>
                    </select>
                    <Input
                      placeholder="Details"
                      value={gearInput.details}
                      onChange={(e) => setGearInput({ ...gearInput, details: e.target.value })}
                      className="col-span-3"
                    />
                    <Button
                      type="button"
                      onClick={addGearItem}
                      size="sm"
                      className="col-span-1 bg-emerald-500 hover:bg-emerald-600"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>

                  {formData.personal_gear.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">No gear added yet</p>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {formData.personal_gear.map((gear, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-white rounded border border-slate-200">
                          <div className="flex-1">
                            <p className="font-medium text-sm">{gear.name}</p>
                            <p className="text-xs text-slate-600">
                              <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded mr-2">{gear.type}</span>
                              {gear.details}
                            </p>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeGearItem(index)}
                            className="text-red-500 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <p className="text-xs text-slate-500">{formData.personal_gear.length} item(s) in gear pack</p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setEditOpen(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" className="btn-gradient">
                  Update Photographer
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
